import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { weekPathPrefix } from "@/lib/week-paths";

/** What the open friend mode publishes for the navbar, which sits above its provider. */
export interface FriendSession {
  friendId: number;
  username: string;
  /** A save failed and the edit only lives in this tab ("No se pudo guardar"). */
  unsaved: boolean;
  /** Drop those edits: leaving would otherwise retry them on unmount. */
  discardUnsaved: () => void;
}

interface FriendModeValue {
  session: FriendSession | null;
  setSession: (update: (prev: FriendSession | null) => FriendSession | null) => void;
  /**
   * Go to `path`. Leaving friend mode with an unsaved change asks first;
   * moving around inside it (other days, weeks) doesn't.
   */
  leaveTo: (path: string) => void;
}

export const FriendModeContext = createContext<FriendModeValue | null>(null);

/**
 * Friend-mode state for the whole app layout. The friend's `WeekPlanProvider`
 * lives inside the friend route, but the person switcher and the banner's
 * Salir both need to know about unsaved changes, so FriendWeekRoute
 * publishes them here. The back button can't be intercepted (no data
 * router); leaving that way relies on the provider's unmount flush.
 */
export function FriendModeProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [session, setSessionState] = useState<FriendSession | null>(null);
  const [pending, setPending] = useState<{ path: string; username: string; discard: () => void } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const setSession = useCallback(
    (update: (prev: FriendSession | null) => FriendSession | null) => setSessionState(update),
    [],
  );

  const leaveTo = useCallback(
    (path: string) => {
      const staysInside = session && path.startsWith(`${weekPathPrefix(session.friendId)}/`);
      if (session?.unsaved && !staysInside) {
        setPending({ path, username: session.username, discard: session.discardUnsaved });
        setConfirmOpen(true);
        return;
      }
      navigate(path);
    },
    [navigate, session],
  );

  const value = useMemo(() => ({ session, setSession, leaveTo }), [session, setSession, leaveTo]);

  return (
    <FriendModeContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Hay cambios sin guardar para ${pending?.username ?? ""}. ¿Salir igual?`}
        description="Tu último cambio no se pudo guardar. Si sales ahora, se pierde."
        cancelText="Quedarme"
        confirmText="Salir sin guardar"
        destructive
        onConfirm={() => {
          setConfirmOpen(false);
          if (!pending) return;
          // "Salir sin guardar" means it: the friend provider's unmount
          // flush must not save these edits once the connection is back.
          pending.discard();
          navigate(pending.path);
        }}
      />
    </FriendModeContext.Provider>
  );
}

export function useFriendMode(): FriendModeValue {
  const ctx = useContext(FriendModeContext);
  if (!ctx) throw new Error("useFriendMode must be used within FriendModeProvider");
  return ctx;
}
